import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8081";

const APPEARANCE = { theme: "stripe" };

// A PaymentIntent created with manual capture settles on `requires_capture`
// (funds authorized, not yet captured) rather than `succeeded`. Both mean the
// customer's payment went through, so the checkout treats them the same.
const PAID_STATUSES = ["succeeded", "requires_capture"];

/**
 * Standalone Stripe checkout wired to the platform's public
 * `POST /v2/checkout/payment-intent` endpoint.
 *
 * The endpoint creates a PaymentIntent as a direct charge on a statically
 * configured Express connected account and returns
 * `{ id, clientSecret, publishableKey, stripeAccount }`. A PaymentIntent
 * client secret pairs with the Payment Element (not Embedded Checkout, which
 * needs a Checkout Session). Stripe.js is initialized with the platform
 * publishable key + the connected account so the Element renders against the
 * right account. `payment_method_types: ["card"]` on the backend limits this
 * to cards + wallets (Apple Pay / Google Pay).
 */
export default function StripeCheckout({ order }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [error, setError] = useState(null);
  // StrictMode invokes effects twice in dev; guard so we create one intent.
  const requested = useRef(false);

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;

    (async () => {
      try {
        const res = await fetch(`${API_URL}/v2/checkout/payment-intent`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_CHECKOUT_API_TOKEN}`,
          },
          body: JSON.stringify({
            name: order.name,
            email: order.email,
            amount: order.amount, // dollars; the backend converts to cents
          }),
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.message || `Request failed (${res.status})`);
        }

        const data = await res.json();
        setStripePromise(
          loadStripe(data.publishableKey, { stripeAccount: data.stripeAccount }),
        );
        setClientSecret(data.clientSecret);
      } catch (err) {
        setError(err.message);
      }
    })();
  }, [order]);

  if (error) {
    return (
      <div className="stripe-placeholder" role="alert">
        <span className="stripe-placeholder__badge">Stripe error</span>
        <p className="stripe-placeholder__text">{error}</p>
        <p className="stripe-placeholder__hint">
          Confirm the API is running on {API_URL} and{" "}
          <code>EQ_STRIPE_STATIC_ACCOUNT_ID</code> is set.
        </p>
      </div>
    );
  }

  if (!clientSecret || !stripePromise) {
    return (
      <div className="stripe-placeholder">
        <span className="stripe-placeholder__badge">Stripe Payment Element</span>
        <p className="stripe-placeholder__text">Loading payment form…</p>
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret, appearance: APPEARANCE }}
    >
      <CheckoutForm order={order} />
    </Elements>
  );
}

function CheckoutForm({ order }) {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!stripe || !elements) return;

      setSubmitting(true);
      setMessage(null);

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: { return_url: window.location.href },
      });

      if (error) {
        setMessage(error.message);
        setSubmitting(false);
      } else if (PAID_STATUSES.includes(paymentIntent?.status)) {
        // Leave `submitting` set so the button stays disabled through the
        // navigation; the success page renders from the in-memory order.
        navigate("/success", { state: { paymentIntentId: paymentIntent.id } });
      } else {
        setMessage(`Payment status: ${paymentIntent?.status ?? "unknown"}`);
        setSubmitting(false);
      }
    },
    [stripe, elements, navigate],
  );

  return (
    <form className="pay-form" onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: "tabs" }} />
      <button
        className="btn pay-form__submit"
        type="submit"
        disabled={!stripe || submitting}
      >
        {submitting ? "Processing…" : `Pay ${formatUsd(order.amount)}`}
      </button>
      {message && (
        <output className="pay-form__message pay-form__message--err">
          {message}
        </output>
      )}
    </form>
  );
}

function formatUsd(amount) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}
